import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { translations } from '@/lib/i18n';
import { api, ADMIN_TOKEN_KEY, CUSTOMER_TOKEN_KEY } from '@/lib/api';

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const CART_KEY = 'nw_cart';

const toLine = (product, quantity) => ({
  product_id: product.id, name: product.name, price_eur: product.price_eur,
  image: (product.images || [])[0], quantity, reference_id: product.reference_id,
  format: product.format, slug: product.slug,
});

const mergeCarts = (server = [], local = []) => {
  const map = new Map();
  [...server, ...local].forEach((i) => {
    if (!i?.product_id) return;
    const prev = map.get(i.product_id);
    map.set(i.product_id, prev ? { ...prev, ...i, quantity: Math.max(prev.quantity, i.quantity) } : { ...i });
  });
  return [...map.values()];
};

export function StoreProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('nw_lang') || 'fr');
  const [currency, setCurrency] = useState(() => localStorage.getItem('nw_currency') || 'EUR');
  const [fx, setFx] = useState({ base: 'EUR', rates: { EUR: 1 }, symbols: { EUR: '\u20ac' } });
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  // admin session
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || null);
  const [admin, setAdmin] = useState(null);
  // customer session
  const [customerToken, setCustomerToken] = useState(() => localStorage.getItem(CUSTOMER_TOKEN_KEY) || null);
  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(() => !!localStorage.getItem(CUSTOMER_TOKEN_KEY));
  const [wishlistIds, setWishlistIds] = useState([]);
  const syncTimer = useRef(null);
  const skipNextSync = useRef(false);
  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  const t = translations[lang];

  useEffect(() => { api.fx().then(setFx).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem('nw_lang', lang); document.documentElement.lang = lang; }, [lang]);
  useEffect(() => { localStorage.setItem('nw_currency', currency); }, [currency]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  // ---- server-side cart persistence for signed-in customers (debounced)
  useEffect(() => {
    if (!customerToken || !customer) return;
    if (skipNextSync.current) { skipNextSync.current = false; return; }
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => { api.customerCartSync(cart).catch(() => {}); }, 600);
    return () => clearTimeout(syncTimer.current);
  }, [cart, customerToken, customer]);

  const convert = (eur) => eur * (fx.rates?.[currency] ?? 1);
  const formatPrice = (eur) => {
    const val = convert(eur);
    const symbol = fx.symbols?.[currency] ?? '\u20ac';
    const formatted = new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', {
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(Math.round(val));
    return currency === 'EUR' ? `${formatted}\u00a0\u20ac` : `${symbol}${formatted}`;
  };

  const addToCart = (product, quantity = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(99, next[idx].quantity + quantity) };
        return next;
      }
      return [...prev, toLine(product, quantity)];
    });
  };
  const updateQty = (productId, quantity) => {
    setCart((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i)).filter((i) => i.quantity > 0));
  };
  const removeFromCart = (productId) => setCart((prev) => prev.filter((i) => i.product_id !== productId));
  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price_eur * i.quantity, 0);

  // ---- live product refresh: keep cart items in sync with any admin edits (price, name, image).
  // Runs on mount, when the tab regains focus, and every 25s while the cart is not empty.
  const hydrateCart = useCallback(async () => {
    const current = cartRef.current;
    if (!current || current.length === 0) return;
    try {
      const { items } = await api.hydrateCart(current.map((i) => ({ product_id: i.product_id, quantity: i.quantity })));
      const map = new Map(items.map((p) => [p.product_id, p]));
      setCart((prev) => {
        const next = prev
          .filter((i) => map.has(i.product_id))
          .map((i) => {
            const p = map.get(i.product_id);
            return { ...i, name: p.name, price_eur: p.price_eur, image: p.image,
              format: p.format, slug: p.slug, reference_id: p.reference_id };
          });
        return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
      });
    } catch (e) { console.debug('Cart hydrate skipped:', e?.message || e); }
  }, []);

  useEffect(() => {
    hydrateCart();
    const onFocus = () => hydrateCart();
    window.addEventListener('focus', onFocus);
    const iv = setInterval(hydrateCart, 25000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(iv); };
  }, [hydrateCart]);

  // ---- admin auth
  const login = async (email, password) => {
    const res = await api.login({ email, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, res.token);
    setToken(res.token); setAdmin(res.user);
    return res;
  };
  const logout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); setToken(null); setAdmin(null); };
  useEffect(() => {
    if (token && !admin) { api.me().then(setAdmin).catch(() => logout()); }
    // eslint-disable-next-line
  }, [token]);

  // ---- customer auth
  const loadWishlist = useCallback(() => api.wishlist().then((d) => setWishlistIds(d.product_ids || [])).catch(() => {}), []);

  const establishCustomerSession = (res) => {
    localStorage.setItem(CUSTOMER_TOKEN_KEY, res.token);
    setCustomerToken(res.token);
    setCustomer(res.customer);
    // merge server cart with the basket built while browsing anonymously
    const merged = mergeCarts(res.cart || [], cart);
    skipNextSync.current = false;
    setCart(merged);
    api.customerCartSync(merged).catch(() => {});
    loadWishlist();
    return res.customer;
  };

  const registerCustomer = async ({ full_name, email, password }) => {
    const res = await api.register({ full_name, email, password, lang });
    return establishCustomerSession({ ...res, cart: [] });
  };
  const loginCustomer = async (email, password) => {
    const res = await api.customerLogin({ email, password });
    return establishCustomerSession(res);
  };
  const logoutCustomer = () => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setCustomerToken(null); setCustomer(null); setWishlistIds([]);
  };
  const refreshCustomer = useCallback(() => api.customerMe().then((c) => { setCustomer(c); return c; }), []);

  useEffect(() => {
    if (!customerToken) { setCustomerLoading(false); return; }
    let active = true;
    setCustomerLoading(true);
    Promise.all([api.customerMe(), api.customerCart()])
      .then(([c, cartRes]) => {
        if (!active) return;
        setCustomer(c);
        const merged = mergeCarts(cartRes.items || [], JSON.parse(localStorage.getItem(CART_KEY) || '[]'));
        skipNextSync.current = true;
        setCart(merged);
        loadWishlist();
      })
      .catch(() => { if (active) logoutCustomer(); })
      .finally(() => { if (active) setCustomerLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [customerToken]);

  const inWishlist = (productId) => wishlistIds.includes(productId);
  const toggleWishlist = async (product) => {
    if (!customer) return 'auth_required';
    if (inWishlist(product.id)) {
      setWishlistIds((ids) => ids.filter((i) => i !== product.id));
      await api.wishlistRemove(product.id).catch(() => loadWishlist());
      return 'removed';
    }
    setWishlistIds((ids) => [...ids, product.id]);
    await api.wishlistAdd(product.id).catch(() => loadWishlist());
    return 'added';
  };

  const value = useMemo(() => ({
    lang, setLang, t, currency, setCurrency, fx, convert, formatPrice,
    cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartSubtotal, hydrateCart,
    token, admin, login, logout,
    customer, customerToken, customerLoading, registerCustomer, loginCustomer, logoutCustomer, refreshCustomer,
    wishlistIds, inWishlist, toggleWishlist, loadWishlist,
    // eslint-disable-next-line
  }), [lang, currency, fx, cart, token, admin, customer, customerToken, customerLoading, wishlistIds]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
